import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Alert,
    Badge
} from 'react-bootstrap';
import { getAuthenticatedAxios } from './axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const ICTCheckoutStage = ({ checkoutStatus, onCheckoutUpdate }) => {
    const { t } = useTranslation('ictCheckoutStage');
    const { currentUser } = useAuth();
    const [devices, setDevices] = useState([]);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [returnConfirmed, setReturnConfirmed] = useState(false);
    const [rejectionDetails, setRejectionDetails] = useState(null);
    const [showEquipmentModal, setShowEquipmentModal] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);

    useEffect(() => {
        if (currentUser && currentUser.id) {
            if (checkoutStatus?.user_return_confirmed) {
                setReturnConfirmed(true);
            } else {
                fetchUserDevices();
            }

            // Fetch rejection history whenever ict_checkout_status is false or null
            // — covers both old backend (false) and new backend (null after reset)
            // Also covers the stuck state where user_return_confirmed=true but ICT rejected
            if (checkoutStatus?.ict_checkout_status === false ||
                checkoutStatus?.ict_checkout_status === null) {
                fetchRejectionHistory();
            }
        }
    }, [currentUser, checkoutStatus]);

    const fetchRejectionHistory = async () => {
        try {
            const authenticatedAxios = getAuthenticatedAxios();
            const response = await authenticatedAxios.get(
                `${BASE_URL}/checkout/rejections/${currentUser.id}?department=ict`
            );

            if (response.data && response.data.length > 0) {
                const rejection = response.data[0];

                // ADDED: Only show rejection if it postdates the last
                // user_return_date — if the staff has already resubmitted
                // after this rejection, ignore the stale record.
                const lastReturn = checkoutStatus?.user_return_date
                    ? new Date(checkoutStatus.user_return_date)
                    : null;
                const lastReset = checkoutStatus?.last_reset_date
                    ? new Date(checkoutStatus.last_reset_date)
                    : null;
                const cutoff = lastReturn && lastReset
                    ? new Date(Math.max(lastReturn, lastReset))
                    : lastReturn || lastReset;
                const rejectionDate = new Date(rejection.created_at);

                if (!cutoff || rejectionDate > cutoff) {
                    setRejectionDetails(rejection);
                } else {
                    // Rejection predates last action — treat as stale
                    setRejectionDetails(null);
                }
            }
        } catch (err) {
            console.error('Error fetching rejection history:', err);
        }
    };

    const fetchUserDevices = async () => {
        try {
            const authenticatedAxios = getAuthenticatedAxios();
            const response = await authenticatedAxios.get(
                `${BASE_URL}/checkout/equipment/${currentUser.id}?include_returned=false`
            );

            const activeDevices = response.data.filter(device =>
                device.status !== 'RETURNED' &&
                device.status !== 'PENDING_VERIFICATION'
            );

            const initializedDevices = activeDevices.map(device => ({
                device_id: device.id,
                make: device.make,
                model: device.model,
                serial_number: device.serial_number,
                status: device.status || 'ASSIGNED',
                isReturned: false,
                returnNotes: ''
            }));

            setDevices(initializedDevices);
        } catch (err) {
            console.error('Device Fetch Error:', err);
            if (err.response?.status === 404) {
                setDevices([]);
            } else {
                setError(err.response?.data?.detail || t('errors.fetch_equipment'));
            }
        }
    };

    const handleEquipmentClick = (device) => {
        setSelectedDevice(device);
        setShowEquipmentModal(true);
    };

    const updateDeviceReturn = (deviceId, field, value) => {
        setDevices(prevDevices => prevDevices.map(device =>
            device.device_id === deviceId ? { ...device, [field]: value } : device
        ));
    };

    const handleConfirmReturn = async () => {
        try {
            setIsProcessing(true);
            setError(null);
            const authenticatedAxios = getAuthenticatedAxios();

            const devicesPayload = devices.map(device => ({
                device_id: device.device_id,
                is_returned: device.isReturned,
                notes: device.returnNotes || ''
            }));

            console.log('Submitting confirm-return with devices:', devicesPayload);

            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/confirm-return`,
                { devices: devicesPayload }
            );

            console.log('confirm-return response:', response.data);

            if (response.data) {
                // Clear rejection details so the component re-renders
                // into the "awaiting verification" state
                setRejectionDetails(null);
                setReturnConfirmed(true);
                setShowConfirmModal(false);
                setError(null);
                setDevices([]);

                // FIX 4: Notify ICT that staff confirmed equipment return
                try {
                    const notifyAxios = getAuthenticatedAxios();
                    await notifyAxios.post(`${BASE_URL}/notify/ict-pending`, {
                        user_id: currentUser.id
                    });
                } catch (emailErr) {
                    console.warn('ICT pending notification failed (non-blocking):', emailErr);
                }

                // onCheckoutUpdate is refreshStatus() in the parent
                if (onCheckoutUpdate) {
                    onCheckoutUpdate();
                }
            }
        } catch (err) {
            console.error('Return Confirmation Error:', err.response?.data || err.message);
            setShowConfirmModal(false);
            // FIXED: show the actual error so staff knows what went wrong
            setError(
                err.response?.data?.detail ||
                err.message ||
                t('errors.fetch_equipment')
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const canSubmitReturn = () => {
        if (devices.length === 0) return true;
        return devices.every(device => device.isReturned === true);
    };

    const handleMarkAllReturned = () => {
        setDevices(prevDevices =>
            prevDevices.map(device => ({
                ...device,
                isReturned: true
            }))
        );
    };

    const renderEquipmentDetails = () => {
        if (!selectedDevice) return null;

        return (
            <div>
                <h5>{t('equipment_details.title')}</h5>
                <Table bordered>
                    <tbody>
                    <tr>
                        <th>{t('equipment_details.make_model')}</th>
                        <td>{selectedDevice.make} {selectedDevice.model}</td>
                    </tr>
                    <tr>
                        <th>{t('equipment_details.serial')}</th>
                        <td>{selectedDevice.serial_number}</td>
                    </tr>
                    <tr>
                        <th>{t('equipment_details.status')}</th>
                        <td>
                            <Badge bg={selectedDevice.status === 'RETURNED' ? 'success' : 'warning'}>
                                {selectedDevice.status || t('equipment_details.assigned')}
                            </Badge>
                        </td>
                    </tr>
                    </tbody>
                </Table>
            </div>
        );
    };

    const renderContent = () => {
        if (rejectionDetails) {
            return (
                <div className="bg-danger text-white p-4 rounded">
                    <h4>{t('rejection.title')}</h4>
                    <div className="mb-3">
                        <strong>{t('rejection.reason')}</strong> {rejectionDetails.reason}
                    </div>
                    <div className="mb-3">
                        <strong>{t('rejection.rejection_date')}</strong> {new Date(rejectionDetails.created_at).toLocaleString()}
                    </div>
                    <div className="mb-3">
                        {t('rejection.instruction')}
                    </div>

                    {/* FIX: Always show the resubmit section after rejection,
                        whether or not the user has equipment */}
                    <div className="mt-4 bg-white text-dark p-4 rounded">
                        {devices.length > 0 ? (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>{t('equipment.assigned_title')}</h5>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleMarkAllReturned}
                                    >
                                        {t('table.mark_all_button')}
                                    </Button>
                                </div>

                                <Table striped bordered hover>
                                    <thead>
                                    <tr>
                                        <th>{t('table.headers.device')}</th>
                                        <th>{t('table.headers.serial')}</th>
                                        <th>{t('table.headers.status')}</th>
                                        <th>{t('table.headers.returned')}</th>
                                        <th>{t('table.headers.notes')}</th>
                                        <th>{t('table.headers.actions')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {devices.map(device => (
                                        <tr key={device.device_id}>
                                            <td>{device.make} {device.model}</td>
                                            <td>{device.serial_number}</td>
                                            <td>
                                                <Badge bg={device.status === 'RETURNED' ? 'success' : 'warning'}>
                                                    {device.status || t('equipment_details.assigned')}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={device.isReturned}
                                                    onChange={(e) => updateDeviceReturn(
                                                        device.device_id,
                                                        'isReturned',
                                                        e.target.checked
                                                    )}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={t('table.notes_placeholder')}
                                                    value={device.returnNotes || ''}
                                                    onChange={(e) => updateDeviceReturn(
                                                        device.device_id,
                                                        'returnNotes',
                                                        e.target.value
                                                    )}
                                                />
                                            </td>
                                            <td>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => handleEquipmentClick(device)}
                                                >
                                                    {t('table.details_button')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </>
                        ) : (
                            <p className="text-muted mb-3">{t('equipment.none_message')}</p>
                        )}

                        {/* Resubmit button — always visible after rejection */}
                        <div className="text-center mt-3">
                            <Button
                                variant="primary"
                                onClick={() => setShowConfirmModal(true)}
                                disabled={!canSubmitReturn()}
                            >
                                {t('buttons.reconfirm')}
                            </Button>
                            {!canSubmitReturn() && devices.length > 0 && (
                                <div className="mt-2">
                                    <small className="text-warning">
                                        {`${t('submission.please_mark_prefix', 'Please mark all equipment as returned to proceed')} (${devices.filter(d => d.isReturned).length}/${devices.length} marked)`}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        if (checkoutStatus?.is_checkout_complete) {
            return (
                <Alert variant="success">
                    <Alert.Heading>
                        <i className="bi bi-check-circle-fill me-2"></i>
                        {t('completion.title')}
                    </Alert.Heading>
                    <p>{t('completion.message')}</p>
                    <hr />
                    <p className="mb-0">
                        <strong>{t('completion.date')}</strong> {new Date(checkoutStatus.checkout_completion_date).toLocaleString()}
                    </p>
                </Alert>
            );
        }

        if (checkoutStatus?.ict_checkout_status === true) {
            return (
                <Alert variant="success">
                    <Alert.Heading>
                        <i className="bi bi-laptop me-2"></i>
                        {t('ict_completed.title')}
                    </Alert.Heading>
                    <p>{t('ict_completed.message')}</p>
                    <hr />
                    <div className="d-flex justify-content-between align-items-center">
                        <p className="mb-0">
                            <strong>{t('ict_completed.date')}</strong> {new Date(checkoutStatus.ict_checkout_date).toLocaleString()}
                        </p>
                        {checkoutStatus.ict_checked_by && (
                            <Badge bg="info">{t('ict_completed.processed_by')} {checkoutStatus.ict_checked_by}</Badge>
                        )}
                    </div>
                    {checkoutStatus.ict_checkout_notes && (
                        <div className="mt-2">
                            <small className="text-muted">
                                <strong>{t('ict_completed.notes')}</strong> {checkoutStatus.ict_checkout_notes}
                            </small>
                        </div>
                    )}
                </Alert>
            );
        }

        if (returnConfirmed) {
            return (
                <Alert variant="info">
                    <Alert.Heading>
                        <i className="bi bi-hourglass-split me-2"></i>
                        {t('awaiting_verification.title')}
                    </Alert.Heading>
                    <p>{t('awaiting_verification.message')}</p>
                    <hr />
                    <div className="mt-3 p-3 bg-light text-dark rounded">
                        <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <span><strong>{t('awaiting_verification.wait_message')}</strong></span>
                        </div>
                    </div>
                </Alert>
            );
        }

        return (
            <>
                <Alert variant="info">
                    <Alert.Heading>{t('equipment.assigned_title')}</Alert.Heading>
                    <p>{t('equipment.description')}</p>
                </Alert>

                {devices.length === 0 ? (
                    <>
                        <Alert variant="warning">
                            <h5>{t('equipment.none_assigned')}</h5>
                            <p>{t('equipment.none_message')}</p>
                        </Alert>
                        <div className="text-center mt-3">
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={() => setShowConfirmModal(true)}
                            >
                                <i className="bi bi-check-circle me-2"></i>
                                {t('buttons.confirm_no_equipment')}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>{t('equipment.assigned_title')}</h5>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleMarkAllReturned}
                            >
                                {t('table.mark_all_button')}
                            </Button>
                        </div>

                        <Table striped bordered hover>
                            <thead>
                            <tr>
                                <th>{t('table.headers.device')}</th>
                                <th>{t('table.headers.serial')}</th>
                                <th>{t('table.headers.status')}</th>
                                <th>{t('table.headers.returned')}</th>
                                <th>{t('table.headers.notes')}</th>
                                <th>{t('table.headers.actions')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {devices.map(device => (
                                <tr key={device.device_id}>
                                    <td>{device.make} {device.model}</td>
                                    <td>{device.serial_number}</td>
                                    <td>
                                        <Badge bg={device.status === 'RETURNED' ? 'success' : 'warning'}>
                                            {device.status || t('equipment_details.assigned')}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={device.isReturned}
                                            onChange={(e) => updateDeviceReturn(
                                                device.device_id,
                                                'isReturned',
                                                e.target.checked
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('table.notes_placeholder')}
                                            value={device.returnNotes || ''}
                                            onChange={(e) => updateDeviceReturn(
                                                device.device_id,
                                                'returnNotes',
                                                e.target.value
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <Button
                                            variant="info"
                                            size="sm"
                                            onClick={() => handleEquipmentClick(device)}
                                        >
                                            {t('table.details_button')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>

                        <div className="text-center mt-3">
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={() => setShowConfirmModal(true)}
                                disabled={!canSubmitReturn()}
                            >
                                <i className="bi bi-check-circle me-2"></i>
                                {t('buttons.confirm_return')}
                            </Button>
                            {!canSubmitReturn() && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        {`${t('submission.please_mark_prefix', 'Please mark all equipment as returned to proceed')} (${devices.filter(d => d.isReturned).length}/${devices.length} marked)`}
                                    </small>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <Card>
            <Card.Header>
                <h3>{t('heading')}</h3>
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <strong>{t('errors.prefix')}</strong> {error}
                    </Alert>
                )}

                {renderContent()}

                <Modal show={showEquipmentModal} onHide={() => setShowEquipmentModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('equipment_details.title')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {renderEquipmentDetails()}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEquipmentModal(false)}>
                            {t('equipment_details.close_button')}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                            {devices.length === 0 ? t('confirmation_modal.no_equipment_modal_title') : rejectionDetails ? t('confirmation_modal.reconfirm_title') : t('confirmation_modal.title')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{devices.length === 0 ? t('confirmation_modal.no_equipment_question') : t('confirmation_modal.question')}</p>
                        <p>{t('confirmation_modal.actions')}</p>
                        <ul>
                            <li>{t('confirmation_modal.action_submit')}</li>
                            <li>{t('confirmation_modal.action_notify')}</li>
                            <li>{t('confirmation_modal.action_lock')}</li>
                        </ul>

                        {devices.length > 0 ? (
                            <div className="mt-3 p-3 bg-light rounded">
                                <h6><strong>{t('confirmation_modal.equipment_title')}</strong></h6>
                                <ul className="mb-0">
                                    {devices.filter(d => d.isReturned).map(device => (
                                        <li key={device.device_id}>
                                            <strong>{device.make} {device.model}</strong> (S/N: {device.serial_number})
                                            {device.returnNotes && (
                                                <div><small className="text-muted">{t('confirmation_modal.notes')} {device.returnNotes}</small></div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="mt-3 p-3 bg-light rounded">
                                <p className="mb-0">
                                    <strong>{t('confirmation_modal.no_equipment_title')}</strong> - {t('confirmation_modal.no_equipment_message')}
                                </p>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowConfirmModal(false)}
                            disabled={isProcessing}
                        >
                            {t('buttons.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleConfirmReturn}
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    {t('buttons.processing')}
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    {rejectionDetails ? t('buttons.reconfirm') : t('buttons.confirm')}
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card.Body>
        </Card>
    );
};

export default ICTCheckoutStage;